// File: src/components/EventPreview/SpeedmatchHostCard.jsx
import React, { useMemo } from 'react';
import { LuShieldCheck, LuMail } from 'react-icons/lu';

const C = { ink: '#0B0B0B', sub: '#6B7280', hair: '#EEF0F2', chip: '#F4F4F5' };

function toLongDate(str = '') {
  if (!str) return '';
  const [y, m, d] = str.split('-').map(Number);
  if (!y) return '';
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * SpeedmatchHostCard
 * Props:
 *  - hostName       (string)  e.g., "Nati K."
 *  - hostAvatar     (string URL)
 *  - promoterEmail  (string email | optional)
 *  - eventName      (string)  used for email subject/context
 *  - eventDate      (YYYY-MM-DD) used for email body
 *  - tenureText     (string)  e.g., "1 year hosting"
 *  - idVerified     (boolean) show the "ID Verified" badge
 */
export default function SpeedmatchHostCard({
  hostName = 'Host',
  hostAvatar,
  promoterEmail = '',
  eventName = '',
  eventDate = '',
  tenureText,
  hostCreatedAt, 
  idVerified = true,
}) {
  function tenureLabelFrom(dateLike) {
    if (!dateLike) return '';
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    let months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
    if (now.getDate() < d.getDate()) months -= 1;
    if (months <= 0) return 'New host';
    if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'} hosting`;
    const years = Math.floor(months / 12);
    return `${years} ${years === 1 ? 'year' : 'years'} hosting`;
 }
  const tenure = tenureText || tenureLabelFrom(hostCreatedAt)
  const avatarUrl =
    hostAvatar || `https://i.pravatar.cc/80?u=${encodeURIComponent(hostName)}`;

  const mailtoHref = useMemo(() => {
    const email = (promoterEmail || '').trim();
    const subject = `${eventName || 'Event'} — Event inquiry`;
    const body = `Hi ${hostName},

I’m reaching out about “${eventName || 'your event'}” on ${toLongDate(eventDate)}.`;
    if (!email) return 'mailto:';
    return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  }, [promoterEmail, hostName, eventName, eventDate]);

  return (
    <>
      <div style={S.hairline} />
      <div style={S.hostCard}>
        <div style={S.hostStrip}>
          <div
            style={{ ...S.hostAvatar, backgroundImage: `url(${avatarUrl})` }}
            aria-label={`${hostName} avatar`}
          />
          <div style={S.hostTextAndBadge}>
            <div>
              <div style={S.hostTitle}>Hosted by {(hostName || '').toUpperCase()}</div>
              <div style={S.hostSub}>{tenure}</div>
            </div>

            {idVerified && (
              <span style={S.idBadge}>
                <LuShieldCheck style={S.idBadgeIcon} aria-hidden />
                ID Verified
              </span>
            )}
          </div>

          <a
            href={mailtoHref}
            onClick={(e) => {
              if (!promoterEmail) {
                e.preventDefault();
                alert('No email is available for this host.');
              }
            }}
            aria-label="Email host"
            title={promoterEmail ? `Email ${hostName}` : 'No email on file'}
            style={{ ...S.emailBtn, textDecoration: 'none' }}
            role="button"
          >
            <LuMail style={{ fontSize: 16, color: '#000' }} />
          </a>
        </div>
      </div>
    </>
  );
}

const S = {
  hairline: {
    height: 1,
    background: C.hair,
    margin: '0 24px 18px',
  },

  hostCard: {
    border: '1px solid ' + C.hair,
    borderRadius: 12,
    background: '#fff',
    padding: '12px 8px',
    margin: '0 24px',
  },

  hostStrip: {
    display: 'grid',
    gridTemplateColumns: '40px 1fr auto',
    alignItems: 'center',
    gap: 4,
  },

  hostTextAndBadge: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: 8,
  },

  idBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 12px',
    borderRadius: 999,
    color: '#E61010',
    background: '#FEECEC',
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    height: 18,
  },
  idBadgeIcon: { fontSize: 16, color: '#E61010' },

  hostAvatar: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    border: '2px solid #fff',
    boxShadow: '0 0 0 1px ' + C.hair,
  },

  hostTitle: {
    fontSize: 12.5,
    fontWeight: 800,
    color: '#111',
  },
  hostSub: {
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.3,
    color: '#222',
  },

  emailBtn: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: C.chip,
    border: '1px solid ' + C.hair,
    display: 'grid',
    placeItems: 'center',
    justifySelf: 'end',
    padding: 0,
    cursor: 'pointer',
    color: '#000',
    marginRight: 4,
  },
};
